import Service from '@ember/service';
import { getOwner } from '@ember/application';
import * as Sentry from '@sentry/ember';

/**
 * MAP (in-file navigation graph — any LLM reads this without external tooling;
 * keep in sync with the code):
 *   role:      service — level-controlled logging (debug/info/warn/error);
 *              dev/test logs to console as before, production routes
 *              warn/error to Sentry instead
 *   extends:   Service (@ember/service)
 *   template:  none
 *   key:       debug(...args), info(...args), warn(...args), error(...args)
 *   uses:      @sentry/ember, getOwner (to read config:environment)
 *   tests:     log-test.js
 */

/**
 * Centralized logging service (maintainability 1.1).
 *
 * Replaces scattered `console.*` calls in shipped code with level-controlled
 * logging. It mirrors the pattern already used by the global handlers in
 * `core-app.js` (`console.* ` in dev, `Sentry.capture*` in production):
 *
 *   - development / test: logs to the browser console EXACTLY as the old
 *     `console.*` calls did (behavior-preserving for local dev).
 *   - production: routes warn/error to Sentry (already initialized in
 *     core-app.js) instead of leaking to the end user's console; debug/info
 *     are dropped entirely.
 *
 * Inject with `@service log` and call `this.log.error(...)` / `.warn(...)` /
 * `.info(...)` / `.debug(...)`. Accepts the same variadic args as `console.*`.
 */
class LogService extends Service {
  get _isProd() {
    return getOwner(this)?.resolveRegistration('config:environment')?.environment === 'production';
  }
  debug(...args) {
    if (!this._isProd) console.debug(...args);
  }
  info(...args) {
    if (!this._isProd) console.info(...args);
  }
  warn(...args) {
    if (this._isProd) {
      Sentry.captureMessage(this._message(args), 'warning');
    } else {
      console.warn(...args);
    }
  }
  error(...args) {
    if (this._isProd) {
      const err = args.find(a => a instanceof Error);
      // A recognised session-expiry 401 is expected behaviour, not a defect:
      // `_unauthorizedError` (lib/utility/ajax) already announced it on the
      // UNAUTHORIZED_EVENT so the session service can log out and redirect.
      // Capturing it here only floods Sentry with auth-expiry noise
      // (EMBER-9RS / EMBER-9RR / EMBER-SCUOLE-MAESTRO-GE …). Any OTHER error —
      // including a 401 that does NOT carry this exact handled signature — is
      // still captured, so a genuinely unhandled auth failure is never hidden.
      if (err && this._isHandledUnauthorized(err)) return;
      if (err) {
        this._sentryCapture(err, {
          extra: {
            log: this._message(args)
          }
        });
      } else {
        // Wrap in an Error so Sentry keeps a stack trace back to this call
        // site: a bare captureMessage arrives with zero frames and is far
        // harder to place than "undefined is not a function" already is.
        this._sentryCapture(new Error(this._message(args)));
      }
    } else {
      console.error(...args);
    }
  }

  /**
   * True only for the beebeeAjax session-expiry 401 signalled by
   * `_unauthorizedError`: it tags the rejection with both `status === 401` and
   * `code === 'UnauthorizedError'` precisely so a caller can recognise it. Both
   * must match — a bare `status: 401`, a different code, or a message that merely
   * mentions 401 is treated as unhandled and still reported.
   */
  _isHandledUnauthorized(err) {
    return err?.status === 401 && err?.code === 'UnauthorizedError';
  }

  /**
   * Single funnel to the Sentry SDK. The `@sentry/ember` namespace import is
   * frozen and cannot be reassigned in tests (see core-app-wave2-test.js), so
   * routing every capture through one overridable method gives tests a seam to
   * observe whether a given error actually reached Sentry.
   */
  _sentryCapture(err, options) {
    Sentry.captureException(err, options);
  }

  /** Collapse variadic console-style args into a single Sentry message string. */
  _message(args) {
    return args.map(a => {
      if (a instanceof Error) return a.message;
      if (typeof a === 'string') return a;
      try {
        return JSON.stringify(a);
      } catch {
        return String(a);
      }
    }).join(' ');
  }
}

export { LogService as default };
//# sourceMappingURL=log.js.map
